import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #currentPage = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.currentPage = 0;
    this.play();
  }

  next() {
    this.currentPage += 1;
  }

  previous() {
    this.currentPage -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get itemsPerPage() {
    const style = getComputedStyle(this.closest('.announcement-bar'));
    const desktop = parseInt(style.getPropertyValue('--desktop-items').trim(), 10) || 1;
    const tablet = parseInt(style.getPropertyValue('--tablet-items').trim(), 10) || 1;
    const mobile = parseInt(style.getPropertyValue('--mobile-items').trim(), 10) || 1;

    if (window.matchMedia('(min-width: 990px)').matches) {
      return desktop;
    }
    if (window.matchMedia('(min-width: 750px)').matches) {
      return tablet;
    }
    return mobile;
  }

  get currentPage() {
    return this.#currentPage;
  }

  set currentPage(page) {
    const itemsPerPage = this.itemsPerPage;
    const slides = this.querySelectorAll('slideshow-slide');
    const numSlides = slides.length;
    if (numSlides === 0 || itemsPerPage === 0) return;
    const numPages = Math.ceil(numSlides / itemsPerPage);

    this.#currentPage = page;

    let relativePage = page % numPages;
    if (relativePage < 0) {
      relativePage += numPages;
    }

    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (slidesContainer) {
      const offset = relativePage * 100;
      slidesContainer.style.transform = `translateX(-${offset}%)`;
    }

    const startIndex = relativePage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    slides.forEach((slide, index) => {
      const isVisible = index >= startIndex && index < endIndex;
      slide.setAttribute('aria-hidden', `${!isVisible}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
